import type { PlanningEntityType } from 'generated';

import type { Event } from './types';

import { create } from 'zustand';

/**
 * Holds the "frozen" drag frame after a drop in the Pragmatic Cards/Roster
 * views: a copy of the dropped card is held snapped into the target cell and
 * the source card stays faded until the API call + refetch settle. Presentation
 * only — no source-of-truth change. Mirrors `frozenDropAtom` in the Timeline.
 */
export interface FrozenCardDrop {
  /**
   * Monotonic per-drop id. Identifies the drop itself, so back-to-back drops of
   * the same card(s) are told apart and the preview starts fresh each time.
   */
  dropId: number;
  /** Ids of the dropped card(s) so their source cards stay faded (no snap-back). */
  eventIds: string[];
  /** Snapped top-left of the target slot in viewport coordinates. */
  position: { x: number; y: number };
  /** Width of the target slot so the preview overlays it exactly. */
  width: number;
  /** Number of dropped cards (>1 renders the multi-select preview). */
  count: number;
  /** Event data to render the card preview. */
  event: Event;
  planningEntityId: string;
  planningEntityType: PlanningEntityType;
}

interface FrozenDropStore {
  frozenDrop: FrozenCardDrop | null;
  setFrozenDrop: (frozenDrop: FrozenCardDrop | null) => void;
}

export const useFrozenDropStore = create<FrozenDropStore>()((set) => ({
  frozenDrop: null,
  setFrozenDrop: (frozenDrop) => set({ frozenDrop })
}));

/**
 * Stable module-level setter, so the drop handler can write the freeze without
 * subscribing to it — writing from a component that reads the whole value would
 * re-render the board inside the drop event, delaying the very paint that shows
 * the frozen card.
 */
export const setFrozenCardDrop = (frozenDrop: FrozenCardDrop | null): void =>
  useFrozenDropStore.getState().setFrozenDrop(frozenDrop);

/** The current frozen drop, for the component that renders the preview. */
export const useFrozenCardDrop = (): FrozenCardDrop | null =>
  useFrozenDropStore((state) => state.frozenDrop);

/**
 * Whether one card is part of the current frozen drop.
 *
 * Cards must read the freeze through this, never the whole value: a card per
 * route × routes per board means subscribing to the object re-renders the whole
 * board on every write — and the write happens in the drop event itself. Zustand
 * compares the selected boolean, so only the cards whose answer actually flipped
 * re-render.
 */
export const useIsCardFrozen = (eventId: string): boolean =>
  useFrozenDropStore(
    (state) => state.frozenDrop?.eventIds.includes(eventId) ?? false
  );
